import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const LOGO_URL = "/logo-kairos.png";
const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/admin/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Erro ao solicitar recuperação');
      }

      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Erro ao solicitar recuperação de senha');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian flex items-center justify-center px-6">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-[400px] h-[400px] bg-kairos-orange/15 rounded-full blur-[120px]"></div>
        <div className="absolute bottom-1/4 right-1/4 w-[300px] h-[300px] bg-kairos-orange/10 rounded-full blur-[100px]"></div>
      </div>

      <div className="relative z-10 max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-10">
          <div className="inline-block mb-6">
            <img src={LOGO_URL} alt="Kairós" className="w-20 h-20 rounded-full object-cover mx-auto shadow-lg shadow-kairos-orange/20" />
          </div>
          <h1 className="font-display text-3xl font-bold text-white mb-2">Kairós</h1>
          <p className="text-gray-400">Encontro de Casais</p>
        </div>

        {/* Card */}
        <div className="bg-[var(--ds-surface)] border border-[var(--ds-border)] rounded-2xl p-8">
          {success ? (
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <h2 className="font-display text-2xl text-white mb-4">Email Enviado!</h2>
              <p className="text-gray-400 mb-8">
                Se o email informado estiver cadastrado, você receberá instruções para redefinir sua senha.
              </p>
              <Link to="/admin/login">
                <Button className="w-full h-12 bg-gradient-to-r from-kairos-orange to-kairos-orange-light text-white font-semibold rounded-lg">
                  Voltar ao Login
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <h2 className="font-display text-2xl text-white text-center mb-2">Recuperar Senha</h2>
              <p className="text-gray-400 text-center mb-8 text-sm">
                Digite seu email para receber as instruções de recuperação
              </p>

              {error && (
                <div className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/30 flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="seu@email.com"
                      required
                      className="w-full h-12 pl-12 pr-4 bg-[var(--ds-background)] border border-[var(--ds-border)] rounded-lg text-white placeholder:text-gray-600 focus:outline-none focus:border-kairos-orange/50 transition-colors"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full h-12 bg-gradient-to-r from-kairos-orange to-kairos-orange-light text-white font-semibold rounded-lg hover:shadow-[0_0_20px_rgba(255,107,0,0.3)] transition-all disabled:opacity-50"
                >
                  {isLoading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    'Enviar Instruções'
                  )}
                </Button>
              </form>

              <Link 
                to="/admin/login" 
                className="flex items-center justify-center gap-2 text-gray-400 hover:text-white mt-6 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Voltar ao login
              </Link>
            </>
          )}
        </div>

        {/* Créditos */}
        <div className="mt-12 pt-6 border-t border-white/10 text-center">
          <p className="text-gray-500 text-xs">
            Desenvolvido por{' '}
            <a 
              href="https://grooverdigital.com.br" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-kairos-orange hover:text-kairos-orange-light transition-colors font-medium"
            >
              Groover Digital e Tecnologia
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
